/** biome-ignore-all lint/suspicious/noConsole: false positive */
import { Location } from '@cobliteam/common-types/src/location';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { useNewsSidebar } from '@cobliteam/hooks/src/useEntity/usePrismic';
import { useTranslation } from '@cobliteam/i18n';
import { PrismicImage, PrismicLink, PrismicRichText } from '@cobliteam/prismic';
import { Button, Divider, Popover, Tag, Tooltip } from '@cobliteam/ui/components';
import { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import { useLocalStorage } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import { Fragment, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { getAnimatedIcon } from '../../AppSidebar/components/animatedIcons';

const NewsFeedFallback = () => {
  return <Button variant='surface' color='danger' size='sm' icon={<Icon name='x' />} aria-label='Ver novidades' />;
};

const NewsFeedContent = () => {
  const history = useHistory();
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { data: news, content: sidebar, isLoading } = useNewsSidebar();
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');

  if (isLoading && !sidebar && !news) return null;
  if (news?.length === 0) return null;

  // Verify if there are new news since the last time the news feed was opened
  const sidebarLastUpdatedTimestamp = sidebar?.last_publication_date ? new Date(sidebar.last_publication_date).getTime() : 0;
  const lastNewsFeedOpenedTimestamp = preferences?.lastNewsFeedOpenedDate ? Number(preferences.lastNewsFeedOpenedDate) : 0;
  const hideNewBadge = sidebarLastUpdatedTimestamp >= lastNewsFeedOpenedTimestamp;

  const handleOpenNewsFeed = (open: boolean) => {
    setIsOpen(open);
    if (open) setPreferences({ ...preferences, lastNewsFeedOpenedDate: sidebarLastUpdatedTimestamp + 1 });
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenNewsFeed}>
      <Tooltip content={t('news_feed.title')} asChild position='right'>
        <PopoverTrigger asChild onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <div className='relative'>
            <Button
              data-testid='app-header-newslist-button'
              className='text-default hover:text-brand'
              variant={isOpen || hideNewBadge ? 'surface' : 'ghost'}
              color='primary'
              size='sm'
              icon={getAnimatedIcon({ icon: 'gift', isHovered })}
              aria-label='Ver novidades'
            />
            {hideNewBadge && (
              <span className='-top-0.5 -right-0.5 absolute flex size-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75' />
                <span className='relative inline-flex size-2 rounded-full bg-brand' />
              </span>
            )}
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align='end' side='right' showArrow={false} className='flex w-96 flex-col gap-4 overflow-y-auto border-gray-6 px-0 pt-4 pb-2 shadow-xl'>
        <ScrollArea className='max-h-[75vh]'>
          {news?.map(({ data, uid }) => (
            <Fragment key={uid}>
              <div className='flex flex-col gap-2 px-5'>
                <Tag label={data.type} variant='outlined' />
                <PrismicRichText field={data.title} className='font-semibold text-primary' />
                <PrismicRichText field={data.resume} className='text-default text-gray-9 text-sm' />
                <PrismicLink field={data.link} target='_blank'>
                  <PrismicImage field={data.image} className='h-40 w-full rounded-md border border-gray-4 object-contain shadow-md' widths={[342, 640]} imgixParams={{ q: 75 }} />
                </PrismicLink>
              </div>
              <Divider className='mt-10 mb-6 bg-gray-6' />
            </Fragment>
          ))}

          <div className='flex items-center justify-end pb-6'>
            <Button variant='link' size='md' onClick={() => history.push(Location.NEWS)}>
              Ver todas as notícias <Icon name='arrow-up-right' className='ml-1' size='xs' />
            </Button>
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

const NewsFeed = () => {
  return (
    <ErrorBoundary
      fallback={<NewsFeedFallback />}
      onError={(error, errorInfo) => {
        DatadogTrack.error(error, {
          component: 'NewsFeed',
          section: 'AppHeader',
          errorInfo,
        });
      }}
    >
      <NewsFeedContent />
    </ErrorBoundary>
  );
};

export default NewsFeed;
